import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { FileText, Sparkles } from "lucide-react";

export default function Content() {
  const [posts, setPosts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState("");
  const [topic, setTopic] = useState("");
  const [keyword, setKeyword] = useState("");
  const [tone, setTone] = useState("Professional & Engaging");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [p, b] = await Promise.all([api.get("/content/posts"), api.get("/businesses")]);
      setPosts(p.data);
      setBusinesses(b.data);
      if (b.data.length && !businessId) setBusinessId(b.data[0].id);
    } catch {}
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const generate = async (e) => {
    e.preventDefault();
    if (!businessId) { setError("Select a business first."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/content/generate", { business_id: businessId, topic, focus_keyword: keyword, tone });
      setTopic(""); setKeyword("");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="border-b border-slate-800 pb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Content Vault</p>
        <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">Content</h1>
        <p className="mt-3 text-neutral-400">SEO-optimized articles auto-published across your business sites.</p>
      </div>

      <form onSubmit={generate} className="border border-slate-800 rounded-xl p-6 bg-slate-900/30 space-y-5">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-sky-400" />
          <h3 className="font-serif text-xl text-sky-100">New article</h3>
        </div>
        {error && <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-3 py-2 text-sm rounded">{error}</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select data-testid="select-biz" value={businessId} onChange={(e) => setBusinessId(e.target.value)} className="bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none">
            <option value="">Select business…</option>
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.business_name}</option>)}
          </select>
          <input data-testid="input-topic" required value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Article topic" className="bg-transparent border-b border-slate-700 focus:border-sky-400 px-1 py-2.5 text-sky-50 outline-none" />
          <input data-testid="input-keyword" required value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Focus keyword" className="bg-transparent border-b border-slate-700 focus:border-sky-400 px-1 py-2.5 text-sky-50 outline-none" />
        </div>
        <button data-testid="btn-generate-content" type="submit" disabled={loading} className="px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded-md font-medium flex items-center gap-2 disabled:opacity-60">
          <Sparkles className="w-4 h-4" /> {loading ? "Publishing…" : "Publish"}
        </button>
      </form>

      <div className="space-y-3">
        {posts.length === 0 && <p className="text-neutral-500 text-center py-16">No articles yet.</p>}
        {posts.map((p) => (
          <div key={p.id} className="border border-slate-800 rounded-lg p-4 hover:border-sky-400/40 transition">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <h4 className="font-serif text-sky-100 truncate">{p.title}</h4>
                <p className="text-[11px] font-mono text-neutral-500 mt-1">Keyword: <span className="text-sky-300/80">{p.focus_keyword}</span> · SEO {p.seo_score} · {p.views} views</p>
              </div>
              <span className="text-[10px] font-mono uppercase text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">Published</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
